import React from "react";
import { toast } from "sonner";
import { WhatsappLogo, Phone, Copy } from "@phosphor-icons/react";
import { normalizeIndianPhone } from "@/lib/phone";

/**
 * Phase 10.4 — Click-to-WhatsApp / Call / Copy quick actions for a phone number.
 *
 * NEVER sends a message automatically. NEVER touches WhatsApp Cloud API.
 * NEVER invokes any backend endpoint. It's a pure `wa.me`/`tel:` deep-link opener.
 *
 * Props:
 *   phone        — raw phone string from CRM/vehicle/user data.
 *   prefill      — optional prefilled message for WhatsApp (default "Hi, this is Q Drives regarding your car enquiry.").
 *   compact      — smaller icon-only buttons for list rows.
 *   className    — extra classes for the wrapping <span>.
 *   testidPrefix — prefix for the data-testid attributes (default "phoneqa").
 *
 * If phone can't be normalized, buttons render disabled with a tooltip.
 */
export default function PhoneQuickActions({
  phone,
  prefill = "Hi, this is Q Drives regarding your car enquiry.",
  compact = false,
  className = "",
  testidPrefix = "phoneqa",
}) {
  const parsed = normalizeIndianPhone(phone);
  const disabled = !parsed;
  const waHref = parsed ? `https://wa.me/${parsed.wa}?text=${encodeURIComponent(prefill)}` : undefined;
  const telHref = parsed ? `tel:${parsed.e164}` : undefined;

  const copy = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!parsed) return;
    const text = parsed.e164;
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text);
      } else {
        // Fallback for older browsers.
        const ta = document.createElement("textarea");
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success(`Copied ${text}`);
    } catch (err) {
      toast.error("Copy failed");
      // eslint-disable-next-line no-console
      console.error("copy phone failed", err);
    }
  };

  const stop = (e) => e.stopPropagation();

  const sizeCls = compact ? "text-[9px] py-1 px-1.5" : "text-[10px] py-1.5 px-2";
  const commonCls =
    `inline-flex items-center gap-1 border tracking-widest uppercase font-bold transition-colors ${sizeCls} ${
      disabled ? "opacity-40 cursor-not-allowed border-[#2A2A2A] text-white/40"
               : "hover:bg-white/5 cursor-pointer"
    }`;

  return (
    <span
      className={`inline-flex items-center gap-1.5 flex-wrap ${className}`}
      data-testid={`${testidPrefix}-actions`}
      onClick={stop}
    >
      {/* WhatsApp */}
      {disabled ? (
        <span data-testid={`${testidPrefix}-wa-disabled`}
              title="Invalid or missing phone number"
              className={`${commonCls} border-emerald-500/50 text-emerald-300/40`}>
          <WhatsappLogo size={compact ? 10 : 12} weight="bold" />{compact ? "" : "WhatsApp"}
        </span>
      ) : (
        <a
          href={waHref}
          target="_blank"
          rel="noopener noreferrer"
          onClick={stop}
          data-testid={`${testidPrefix}-wa`}
          title={`Open WhatsApp chat with ${parsed.display}`}
          className={`${commonCls} border-emerald-500 text-emerald-300 hover:bg-emerald-500/10`}
        >
          <WhatsappLogo size={compact ? 10 : 12} weight="bold" />{compact ? "" : "WhatsApp"}
        </a>
      )}

      {/* Call */}
      {disabled ? (
        <span data-testid={`${testidPrefix}-call-disabled`}
              title="Invalid or missing phone number"
              className={`${commonCls} border-white/20 text-white/30`}>
          <Phone size={compact ? 10 : 12} weight="bold" />{compact ? "" : "Call"}
        </span>
      ) : (
        <a
          href={telHref}
          onClick={stop}
          data-testid={`${testidPrefix}-call`}
          title={`Call ${parsed.display}`}
          className={`${commonCls} border-white/30 text-white/85`}
        >
          <Phone size={compact ? 10 : 12} weight="bold" />{compact ? "" : "Call"}
        </a>
      )}

      {/* Copy */}
      <button
        type="button"
        onClick={copy}
        disabled={disabled}
        data-testid={`${testidPrefix}-copy`}
        title={parsed ? `Copy ${parsed.e164}` : "No phone to copy"}
        className={`${commonCls} border-white/30 text-white/85 bg-transparent`}
      >
        <Copy size={compact ? 10 : 12} weight="bold" />{compact ? "" : "Copy"}
      </button>
    </span>
  );
}
